import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { fadeInUpAnimation } from 'app/app-layout/animations';
import { AppRoute } from 'app/app-layout/constants';
import { EvaluationInterface } from 'app/assessment/evaluation/models';
import { EvaluationService } from 'app/assessment/evaluation/services/evaluation.service';
import { ReviewInterface } from 'app/assessment/models/review.model';
import { AuthService } from 'app/core/authentication/services';
import { RouterRequest } from 'app/core/browser/models/router-request.model';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { UserInterface } from 'app/core/user/models';
import { UserService } from 'app/core/user/services';
import { EmployeeEquipmentWorkflowInterface } from 'app/equipment/models';
import { RecommendationInterface } from 'app/recommendation/models';
import { RecommendationCategoryInterface } from 'app/recommendation/models/recommendation-category.model';
import { RecommendationService } from 'app/recommendation/services/recommendation.service';
import { NameLinkOptions } from 'app/shared/components/user-name-link/user-name-link.component';
import { getDeepCopyOfObject } from 'app/shared/utilities/utility-methods.utility';
import { TaskInterface } from 'app/task/models';
import { TaskStatus } from 'app/task/models/task-status.enum';
import { TaskService } from 'app/task/services';
import { BehaviorSubject, Subject } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cz-evaluation',
  templateUrl: './evaluation.component.html',
  styleUrls: ['./evaluation.component.scss'],
  animations: [fadeInUpAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationComponent implements OnInit, OnDestroy {
  public currentEvaluation$: BehaviorSubject<Readonly<EvaluationInterface>>;
  public reviews$: BehaviorSubject<Readonly<ReviewInterface[]>>;
  public recommendationTemplates$: BehaviorSubject<Readonly<RecommendationCategoryInterface[]>>;
  public employeeWorkFlows$: BehaviorSubject<Readonly<EmployeeEquipmentWorkflowInterface[]>>;
  public recommendations$: BehaviorSubject<Readonly<RecommendationInterface[]>>;
  public adminUsers$: BehaviorSubject<Readonly<UserInterface[]>>;

  public evaluationLoaded: boolean = false;
  public currentUser: UserInterface;
  public currentTask: TaskInterface;
  public userId: number;

  public badgeConfig: NameLinkOptions = {
    showBadge: true,
    badgeOnRight: false,
    showLogout: false,
    showUserName: true,
    linkToProfile: true,
  };
  public recoArry: RecommendationInterface[] = [];
  public recoArryAcount: number = 0;
  private readonly _destroy$: Subject<boolean> = new Subject<boolean>();
// Inject TranslateService
  constructor(
    private readonly _evaluationService: EvaluationService,
    private readonly _recommendationService: RecommendationService,
    private readonly _userService: UserService,
    private readonly _taskService: TaskService,
    private readonly _browserService: BrowserService,
    private readonly _authService: AuthService,
  ) {
    this.currentEvaluation$ = new BehaviorSubject<Readonly<EvaluationInterface>>(undefined);
    this.reviews$ = new BehaviorSubject<Readonly<ReviewInterface[]>>(undefined);
    this.recommendationTemplates$ = new BehaviorSubject<Readonly<RecommendationCategoryInterface[]>>(undefined);
    this.recommendations$ = new BehaviorSubject<Readonly<RecommendationInterface[]>>(undefined);
    this.adminUsers$ = new BehaviorSubject<Readonly<UserInterface[]>>(undefined);
    this.employeeWorkFlows$ = new BehaviorSubject<Readonly<EmployeeEquipmentWorkflowInterface[]>>(undefined);
  }

  public ngOnInit(): void {
    // query the store for the current evaluation's related task
    this._evaluationService.getCurrentEvaluationRelatedTask()
      .pipe(take(1))
      .subscribe((task: TaskInterface) => {
        if(!task) {
          // redirect to the dashboard
          this._browserService.to(new RouterRequest({ route: AppRoute.myZone }));
          return;
        }

        this.currentTask = task;
        let evaluationId: number = null;
        if (task.relatedEntity.entityId > 0) {
          evaluationId = task.relatedEntity.entityId;
        }

        // fetch evaluation for the task
        this._evaluationService.getCurrentEvaluation(task.subject.id, evaluationId)
          .pipe(
            skip(1),
            takeUntil(this._destroy$))
          .subscribe((evaluation: EvaluationInterface) => {
            if (!evaluation) { return; }

            // Check to see if there is a lastUpdated property on the incoming evaluation.
            // If there is a lastUpdated property, we know that it is not first load.
            // if (evaluation.lastUpdated) {
            //   // Static text to Language Variable updated
            //   this._snackBar.open(this._translateService.instant('$message_eval_saved'), undefined, {
            //     duration: 2000,
            //   });
            // }

            this.recommendations$.next(evaluation.analysis.recommendations);

            // Skip the bootstrapping process if we already have a value for currentEvaluation.
            if (this.currentEvaluation$.value) { return; }

            this.currentEvaluation$.next(evaluation);
            this.reviews$.next(evaluation.reviews);
            this.userId = this.currentEvaluation$.value.subject.id;
            this.evaluationLoaded = true;

            // Evaluate if it is necessary to update the related task with evaluation or state transition information.
            if (!task.isComplete) {
              let updateTask: boolean = false;
              const taskCopy: TaskInterface = getDeepCopyOfObject(task);
              if(taskCopy.relatedEntity.entityId < 1) {
                taskCopy.relatedEntity.entityId = evaluation.id;
                updateTask = true;
              }
              if (taskCopy.status === TaskStatus.NEW) {
                taskCopy.status = TaskStatus.IN_PROGRESS;
                updateTask = true;
              }
              if(updateTask) {
                this._taskService.update(taskCopy);
              }
            }
          });
      });

    this._authService.getAuthenticatedUser().pipe(takeUntil(this._destroy$))
      .subscribe((authenticatedUser: UserInterface) => {
        if (!authenticatedUser) { return; }
        this.currentUser = authenticatedUser;
      });

    this._userService.findAllAdminUsers().pipe(takeUntil(this._destroy$))
      .subscribe((adminUsers: UserInterface[]) => {
        if (!adminUsers) { return; }
        this.adminUsers$.next(adminUsers);
      });

    this._recommendationService.getRecommendationTemplatesLibrary()
      .pipe(
        takeUntil(this._destroy$))
      .subscribe((recommendationTemplates: RecommendationCategoryInterface[]) => {
        if (!recommendationTemplates) { return; }

        this.recommendationTemplates$.next(recommendationTemplates);
      });

  }

  public ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  /**
   * Handles a new review.
   *
   * @param review Review
   */
  public handleReviewAdded(review: ReviewInterface): void {
    this._evaluationService.addNewReviewComment(
      {
        text: review.text,
        userId: this.userId,
        evalId: this.currentEvaluation$.value.id,
      },
    )
    .pipe(
      takeUntil(this._destroy$))
    .subscribe((evaluation: EvaluationInterface) => {
      if (!evaluation || evaluation.reviews.length <= 0) { return; }
      this.reviews$.next(evaluation.reviews);
    });
  }

  /**
   * Handle updates to a review.
   *
   * @param review Review
   */
  public handleReviewUpdated(review: ReviewInterface): void {
    this._evaluationService.updateReviewComment(
      {
        review,
        userId: this.userId,
        evalId: this.currentEvaluation$.value.id,
      },
    )
    .pipe(
      takeUntil(this._destroy$))
    .subscribe((evaluation: EvaluationInterface) => {
      if (!evaluation || evaluation.reviews.length <= 0) { return; }
      this.reviews$.next(evaluation.reviews);
    });
  }

  /**
   * Handle removing a review.
   *
   * @param review Review
   */
  public handleReviewRemoved(review: ReviewInterface): void {
    this._evaluationService.deleteReviewComment(
      {
        review,
        userId: this.userId,
        evalId: this.currentEvaluation$.value.id,
      },
    )
    .pipe(
      takeUntil(this._destroy$))
    .subscribe((evaluation: EvaluationInterface) => {
      if (!evaluation) { return; }
      this.reviews$.next(evaluation.reviews);
    });
  }

  /**
   * Handle adding new recommendation.
   *
   * @param recommendation Recommendation
   */
  public handleRecommendationAdded(recommendation: RecommendationInterface): void {
    this._evaluationService.addRecommendation(this.currentEvaluation$.value, recommendation)
      .pipe(takeUntil(this._destroy$))
      .subscribe((evaluation: EvaluationInterface) => {
        if (!evaluation || !evaluation.analysis.recommendations) { return; }

        this.recommendations$.next(evaluation.analysis.recommendations);
      });
  }

  /**
   * Handle updates to the recommendation.
   *
   * @param recommendation Recommendation
   */
  public handleRecommendationUpdated(recommendation: RecommendationInterface): void {
    // tslint:disable-next-line:max-line-length
    if(this.recommendations$.value.length === this.recoArry.length ||(this.recoArryAcount>0 && this.recommendations$.value.length!==this.recoArryAcount)) {
      this.recoArry= [];
      this.recoArryAcount= this.recommendations$.value.length;
    }
    this.recoArry.push(recommendation);
    if(this.recommendations$.value.length===this.recoArry.length) {
      this.recoArryAcount= this.recoArry.length;

      this._evaluationService.updateRecommendationsAll(this.recoArry)
      .pipe(takeUntil(this._destroy$))
      .subscribe((evaluation: EvaluationInterface) => {
        if (!evaluation || !evaluation.analysis.recommendations) { return; }
      });
    }
  }

  /**
   * Handle updates to the recommendation.
   *
   * @param recommendation Recommendation
   */
  public handleRecommendationRemoved(recommendation: RecommendationInterface): void {
    this._evaluationService.removeRecommendations(recommendation)
      .pipe(takeUntil(this._destroy$))
      .subscribe((evaluation: EvaluationInterface) => {
        if (!evaluation || !evaluation.analysis.recommendations) { return; }

        this.recommendations$.next(evaluation.analysis.recommendations);
      });
  }

}
